import { DeleteOutlined } from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { ProjectPhaseStatus } from "../../enum/common";
import {
  IProductionProjectPhases,
  IUpdateProductionProjectPhase,
} from "../../models/Production";
import {
  useDeleteProductionProjectPhaseMilestone,
  useUpdateProductionProjectPhases,
} from "../../query/ProductionQuery";
import { notify } from "../../utils/toast";

interface IModalEditPhaseProps {
  open: boolean;
  onCancel: () => void;
  phase: IProductionProjectPhases | null;
}

const ModalEditProductionProjectPhase = ({
  open,
  onCancel,
  phase,
}: IModalEditPhaseProps) => {
  const [form] = Form.useForm();
  const updatePhaseMutation = useUpdateProductionProjectPhases();
  const deletePhaseMutation = useDeleteProductionProjectPhaseMilestone();

  useEffect(() => {
    if (phase && open) {
      form.setFieldsValue({
        phaseOrder: phase.phaseOrder,
        phaseName: phase.phaseName,
        startDate: dayjs(phase.startDate),
        endDate: dayjs(phase.endDate),
        status: phase.status,
        progress: phase.progress,
        projectPhaseMilestones: phase.prdProjectPhaseMilestones.map((m) => ({
          ...m,
          targetDate: dayjs(m.targetDate),
          completedDate: m.completedDate ? dayjs(m.completedDate) : null,
        })),
      });
    }
  }, [phase, open, form]);

  const handleSubmit = async () => {
    if (!phase) {
      notify.error("Dự án không tồn tại");
      return;
    }
    try {
      const values = await form.validateFields();
      const data: IUpdateProductionProjectPhase = {
        phaseOrder: values.phaseOrder,
        phaseName: values.phaseName,
        startDate: values.startDate.format("YYYY-MM-DD"),
        endDate: values.endDate.format("YYYY-MM-DD"),
        status: values.status,
        progress: values.progress,
        prdProjectPhaseMilestones: values.projectPhaseMilestones.map(
          (m: any) => ({
            id: m.id || 0,
            prdProjectPhaseId: phase!.id,
            milestoneName: m.milestoneName,
            targetDate: dayjs(m.targetDate).format("YYYY-MM-DD"),
            isCompleted: m.isCompleted || false,
            completedDate: m.completedDate
              ? dayjs(m.completedDate).format("YYYY-MM-DD")
              : null,
          }),
        ),
      };

      await updatePhaseMutation.mutateAsync({ id: phase.id, data });
      notify.success("Cập nhật giai đoạn thành công");
      onCancel();
      form.resetFields();
    } catch (error) {
      notify.error("Cập nhật giai đoạn thất bại");
    }
  };
  const handleDeleteMilestone = async (id: number) => {
    try {
      await deletePhaseMutation.mutateAsync({
        id: id,
        projectId: phase?.prdProjectId!,
      });
      notify.success("Xóa cột mốc thành công");
      form.setFieldsValue({
        projectPhaseMilestones: form
          .getFieldsValue()
          .projectPhaseMilestones.filter((m: any) => m.id !== id),
      });
    } catch (error) {
      notify.error("Xóa cột mốc thất bại");
    }
  };

  const getColumns = (remove: (index: number) => void) => [
    {
      title: "Tên cột mốc",
      dataIndex: "milestoneName",
      key: "milestoneName",
      render: (_: any, record: any) => (
        <Form.Item
          name={[record.name, "milestoneName"]}
          rules={[{ required: true, message: "Vui lòng nhập tên cột mốc" }]}
          style={{ marginBottom: 0 }}
        >
          <Input placeholder="Nhập tên cột mốc" />
        </Form.Item>
      ),
    },
    {
      title: "Ngày mục tiêu",
      dataIndex: "targetDate",
      key: "targetDate",
      width: 200,
      render: (_: any, record: any) => (
        <Form.Item
          name={[record.name, "targetDate"]}
          rules={[{ required: true, message: "Vui lòng chọn ngày" }]}
          style={{ marginBottom: 0 }}
        >
          <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
        </Form.Item>
      ),
    },
    {
      title: "Hoàn thành",
      dataIndex: "isCompleted",
      key: "isCompleted",
      width: 120,
      render: (_: any, record: any) => (
        <Form.Item
          name={[record.name, "isCompleted"]}
          style={{ marginBottom: 0 }}
        >
          <Select style={{ width: "100%" }}>
            <Select.Option value={true}>Có</Select.Option>
            <Select.Option value={false}>Không</Select.Option>
          </Select>
        </Form.Item>
      ),
    },
    {
      title: "Ngày hoàn thành",
      dataIndex: "completedDate",
      key: "completedDate",
      width: 200,
      render: (_: any, record: any) => (
        <Form.Item
          name={[record.name, "completedDate"]}
          style={{ marginBottom: 0 }}
        >
          <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
        </Form.Item>
      ),
    },
    {
      title: "",
      key: "action",
      width: 60,
      render: (_: any, record: any) => {
        const milestoneId = form.getFieldValue([
          "projectPhaseMilestones",
          record.name,
          "id",
        ]);
        return (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteMilestone(milestoneId)}
            disabled={!milestoneId}
          />
        );
      },
    },
  ];

  return (
    <Modal
      title="Chỉnh sửa giai đoạn"
      open={open}
      onCancel={onCancel}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Hủy
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={updatePhaseMutation.isPending}
        >
          Cập nhật
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Space direction="vertical" style={{ width: "100%" }} size={16}>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            <Form.Item
              label="Thứ tự giai đoạn"
              name="phaseOrder"
              rules={[{ required: true, message: "Vui lòng nhập thứ tự" }]}
            >
              <InputNumber style={{ width: "100%" }} min={1} />
            </Form.Item>

            <Form.Item
              label="Tên giai đoạn"
              name="phaseName"
              rules={[
                { required: true, message: "Vui lòng nhập tên giai đoạn" },
              ]}
            >
              <Input placeholder="Nhập tên giai đoạn" />
            </Form.Item>
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            <Form.Item
              label="Ngày bắt đầu"
              name="startDate"
              rules={[
                { required: true, message: "Vui lòng chọn ngày bắt đầu" },
              ]}
            >
              <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
            </Form.Item>

            <Form.Item
              label="Ngày kết thúc"
              name="endDate"
              rules={[
                { required: true, message: "Vui lòng chọn ngày kết thúc" },
              ]}
            >
              <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
            </Form.Item>
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            <Form.Item
              label="Trạng thái"
              name="status"
              rules={[{ required: true, message: "Vui lòng chọn trạng thái" }]}
            >
              <Select>
                <Select.Option value={ProjectPhaseStatus.PENDING}>
                  Chờ thực hiện
                </Select.Option>
                <Select.Option value={ProjectPhaseStatus.IN_PROGRESS}>
                  Đang thực hiện
                </Select.Option>
                <Select.Option value={ProjectPhaseStatus.COMPLETED}>
                  Hoàn thành
                </Select.Option>
                <Select.Option value={ProjectPhaseStatus.DELAYED}>
                  Trễ hạn
                </Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Tiến độ (%)"
              name="progress"
              rules={[{ required: true, message: "Vui lòng nhập tiến độ" }]}
            >
              <InputNumber style={{ width: "100%" }} min={0} max={100} />
            </Form.Item>
          </div>

          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <Typography.Text strong>Cột mốc quan trọng</Typography.Text>
            </div>

            <Form.List name="projectPhaseMilestones">
              {(fields, { remove }) => (
                <>
                  {fields.length > 0 ? (
                    <Table
                      dataSource={fields}
                      columns={getColumns(remove)}
                      pagination={false}
                      rowKey="key"
                      size="small"
                    />
                  ) : (
                    <Empty
                      description="Chưa có cột mốc nào"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )}
                </>
              )}
            </Form.List>
          </div>
        </Space>
      </Form>
    </Modal>
  );
};

export default ModalEditProductionProjectPhase;
